import React, { useMemo } from "react";
import { Button } from "primereact/button";
import { BaseModal } from "../../components/Modal/BaseModal";
import { Toast } from "primereact/toast";
import { utils } from "../../utils/utils";
import { Code, Copy } from "lucide-react";

interface JsonPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  jsonData: any;
  toastRef?: React.RefObject<Toast>;
}

const JsonPreviewModal: React.FC<JsonPreviewModalProps> = ({
  isOpen,
  onClose,
  title,
  jsonData,
  toastRef
}) => {
  const formattedJson = useMemo(() => {
    return utils.formatJsonForDisplay(jsonData);
  }, [jsonData]);

  const handleCopy = () => {
    utils.copyJsonToClipboard(
      jsonData,
      () => {
        if (toastRef?.current) {
          toastRef.current.show({
            severity: "success",
            summary: "Copied",
            detail: "JSON copied to clipboard",
            life: 2000
          });
        }
      },
      () => {
        if (toastRef?.current) {
          toastRef.current.show({
            severity: "error",
            summary: "Error",
            detail: "Failed to copy to clipboard",
            life: 2000
          });
        }
      }
    );
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      icon={<Code size={20} />}
      iconColor="var(--primary-500, #667eea)"
      width="80rem"
      closeOnBackdrop={true}
      closeOnEscape={true}
      showCloseButton={true}
      className="json-modal modal-xl"
    >
      <div className="relative min-h-[300px] max-h-[65vh]">
        <div className="relative bg-gray-100 border border-gray-300 rounded-lg p-2 overflow-hidden h-full flex flex-col">
          {/* Copy button positioned absolutely at top right */}
          <div className="absolute top-4 right-4 z-20">
            <Button
              icon={<Copy size={16} />}
              onClick={handleCopy}
              className="!bg-white !border !border-gray-400 !text-gray-700 !shadow-md w-8 h-8 min-w-[32px] min-h-[32px] p-0 rounded flex items-center justify-center hover:!bg-gray-50 hover:!border-gray-500 hover:!text-gray-800 hover:!shadow-lg active:!shadow-md transition-all duration-150 [&_.p-button-icon]:!text-sm [&_.p-button-icon]:!text-gray-700 [&_.p-button-icon]:!m-0 [&_.p-button-label]:!hidden"
              tooltip="Copy JSON"
              tooltipOptions={{ position: 'left' }}
              text
              rounded
            />
          </div>
          {/* Scrollable JSON content */}
          <div className="relative bg-gray-50 rounded-md overflow-auto flex-1 min-h-[300px] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-track]:bg-gray-200 [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded hover:[&::-webkit-scrollbar-thumb]:bg-gray-500">
            <pre className="font-mono text-xs md:text-sm leading-relaxed m-0 whitespace-pre break-words text-gray-800 bg-gray-50 p-4 md:p-5">
              {formattedJson}
            </pre>
          </div>
        </div>
      </div>
    </BaseModal>
  );
};

export default JsonPreviewModal;

